import { useMemo } from 'react';
import { TextField, type TextFieldProps } from '@mui/material';
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';

/**
 * Convenção de payload: o valor armazenado é a representação `string` com
 * ponto decimal (ex.: `"1234.56"`) — compatível com DECIMAL(15,2) no backend.
 * O visual usa vírgula e separador de milhar.
 *
 * O componente adota o modelo **shift-right**: a cada tecla, todos os dígitos
 * digitados formam o número, e os últimos dois são os centavos. Consequência:
 * apagar/inserir dígitos no meio do valor desloca a vírgula. Este é o padrão
 * de inputs monetários BR (caixas eletrônicos, internet banking) e o usuário
 * espera esse comportamento. Cobertura em `MoneyInput.test.tsx`.
 */

/**
 * "1.234,56" → "1234.56"; "-1.234,56" → "-1234.56". Vazio → "".
 *
 * Sinal negativo: qualquer `-` no texto marca o valor como negativo (permite
 * digitar o menos em qualquer posição). Apagar o `-` volta a positivo. O sinal
 * só é emitido quando há magnitude — nunca produz "-0.00".
 */
export function brlToCanonical(formatted: string): string {
  if (!formatted) return '';
  const neg = formatted.includes('-');
  const digits = formatted.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
  if (!digits) return '';
  const padded = digits.padStart(3, '0');
  const canonical = `${padded.slice(0, -2)}.${padded.slice(-2)}`;
  return neg && Number(canonical) !== 0 ? `-${canonical}` : canonical;
}

/** "1234.56" → "1.234,56"; "-1234.56" → "-1.234,56". Vazio/inválido → "". */
export function canonicalToBrl(canonical: string | number | null | undefined): string {
  if (canonical === null || canonical === undefined || canonical === '') return '';
  const str = typeof canonical === 'number' ? canonical.toFixed(2) : String(canonical);
  if (!/^-?\d+(\.\d{1,2})?$/.test(str)) return '';
  const neg = str.startsWith('-') && Number(str) !== 0;
  const abs = str.replace(/^-/, '');
  const [int, dec = ''] = abs.split('.');
  const decPadded = (dec + '00').slice(0, 2);
  const formatted = `${(int ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decPadded}`;
  return neg ? `-${formatted}` : formatted;
}

export interface MoneyInputProps extends Omit<TextFieldProps, 'value' | 'onChange'> {
  /** Valor canônico (ex.: "1234.56"). Vazio = "". */
  value: string;
  /** Recebe valor canônico (ponto decimal). */
  onChange: (canonical: string) => void;
}

export function MoneyInput({ value, onChange, InputProps, ...rest }: MoneyInputProps): JSX.Element {
  const display = useMemo(() => canonicalToBrl(value), [value]);
  return (
    <TextField
      {...rest}
      value={display}
      onChange={(e) => onChange(brlToCanonical(e.target.value))}
      InputProps={{
        startAdornment: <span style={{ marginRight: 4 }}>R$</span>,
        ...InputProps,
      }}
      inputProps={{
        inputMode: 'decimal',
        autoComplete: 'off',
        ...rest.inputProps,
      }}
    />
  );
}

export interface MoneyInputFieldProps<TFieldValues extends FieldValues> extends Omit<
  MoneyInputProps,
  'value' | 'onChange'
> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
}

export function MoneyInputField<TFieldValues extends FieldValues>({
  control,
  name,
  ...rest
}: MoneyInputFieldProps<TFieldValues>): JSX.Element {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <MoneyInput
          {...rest}
          value={(field.value as string | undefined) ?? ''}
          onChange={(canonical) => field.onChange(canonical)}
          onBlur={field.onBlur}
          inputRef={field.ref}
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message ?? rest.helperText}
        />
      )}
    />
  );
}
